import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import AdminPage from "./AdminPage";
import { apiClient } from "../utils/apiClient";
import type { FeatureFlag } from "@services/featureFlags/types";

interface UserOption {
  id: string;
  email: string;
  name: string;
}

interface PermissionGroupOption {
  id: string;
  name: string;
}

type TargetType = "everyone" | "admins" | "userList" | "permissionGroup";

export function AdminFeatureFlagsPage() {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);

  // Form states
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formEnabled, setFormEnabled] = useState(false);
  const [formTargetType, setFormTargetType] = useState<TargetType>("everyone");
  const [formValue, setFormValue] = useState("true");
  const [formTargetUserIds, setFormTargetUserIds] = useState<string[]>([]);
  const [formTargetGroupId, setFormTargetGroupId] = useState("");

  // Data for pickers
  const [allUsers, setAllUsers] = useState<UserOption[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroupOption[]>([]);

  useEffect(() => {
    fetchFlags();
    fetchUsers();
    fetchPermissionGroups();
  }, []);

  const fetchFlags = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.post<{ flags: FeatureFlag[] }>(
        "/feature-flags/list",
        {},
      );
      setFlags(res.flags);
    } catch (err) {
      console.error("Error fetching feature flags:", err);
      setError("Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiClient.post<{
        users: { id: string; email: string; name: string }[];
      }>("/admin/list_users", {});
      setAllUsers(
        res.users.map((u) => ({ id: u.id, email: u.email, name: u.name })),
      );
    } catch {
      // non-critical
    }
  };

  const fetchPermissionGroups = async () => {
    try {
      const res = await apiClient.post<{
        groups: { id: string; name: string }[];
      }>("/admin/permissions/list", {});
      setPermissionGroups(res.groups.map((g) => ({ id: g.id, name: g.name })));
    } catch {
      // non-critical
    }
  };

  const openCreateDialog = () => {
    setEditingFlag(null);
    setFormName("");
    setFormDescription("");
    setFormEnabled(false);
    setFormValue("true");
    setFormTargetType("everyone");
    setFormTargetUserIds([]);
    setFormTargetGroupId("");
    setDialogOpen(true);
  };

  const openEditDialog = (flag: FeatureFlag) => {
    setEditingFlag(flag);
    setFormName(flag.name);
    setFormDescription(flag.description || "");
    setFormEnabled(flag.enabled);
    setFormValue(JSON.stringify(flag.value));
    setFormTargetType(flag.targetType as TargetType);
    setFormTargetUserIds(flag.targetUserIds);
    setFormTargetGroupId(flag.targetGroupId || "");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (editingFlag) {
        let parsedValue: any;
        try {
          parsedValue = JSON.parse(formValue);
        } catch {
          parsedValue = formValue;
        }
        const res = await apiClient.put<{ flag: FeatureFlag }>(
          "/feature-flags/update",
          {
            id: editingFlag.id,
            name: formName.trim(),
            description: formDescription.trim() || null,
            enabled: formEnabled,
            value: parsedValue,
            targetType: formTargetType,
            targetUserIds: formTargetType === "userList" ? formTargetUserIds : [],
            targetGroupId:
              formTargetType === "permissionGroup" ? formTargetGroupId || null : null,
          },
        );
        setFlags(flags.map((f) => (f.id === editingFlag.id ? res.flag : f)));
      } else {
        let parsedCreateValue: any;
        try {
          parsedCreateValue = JSON.parse(formValue);
        } catch {
          parsedCreateValue = formValue;
        }
        const res = await apiClient.post<{ flag: FeatureFlag }>(
          "/feature-flags/create",
          {
            name: formName.trim(),
            description: formDescription.trim() || undefined,
            enabled: formEnabled,
            value: parsedCreateValue,
            targetType: formTargetType,
            targetUserIds: formTargetType === "userList" ? formTargetUserIds : [],
            targetGroupId:
              formTargetType === "permissionGroup" ? formTargetGroupId || undefined : undefined,
          },
        );
        setFlags([res.flag, ...flags]);
      }
      setDialogOpen(false);
    } catch (err: any) {
      setError(err.message || "Failed to save feature flag");
    }
  };

  const handleToggleEnabled = async (flag: FeatureFlag) => {
    try {
      const res = await apiClient.put<{ flag: FeatureFlag }>(
        "/feature-flags/update",
        { id: flag.id, enabled: !flag.enabled },
      );
      setFlags(flags.map((f) => (f.id === flag.id ? res.flag : f)));
    } catch (err: any) {
      setError(err.message || "Failed to toggle flag");
    }
  };

  const handleDelete = async () => {
    if (!selectedFlag) return;
    try {
      await apiClient.post("/feature-flags/delete", { id: selectedFlag.id });
      setFlags(flags.filter((f) => f.id !== selectedFlag.id));
      setDeleteConfirmOpen(false);
      setSelectedFlag(null);
    } catch (err: any) {
      setError(err.message || "Failed to delete feature flag");
    }
  };

  const getTargetDescription = (flag: FeatureFlag) => {
    switch (flag.targetType) {
      case "everyone":
        return "Everyone";
      case "admins":
        return "Admins only";
      case "userList":
        return `${flag.targetUserIds.length} user(s)`;
      case "permissionGroup":
        return flag.targetGroupName || flag.targetGroupId || "No group";
      default:
        return flag.targetType;
    }
  };

  return (
    <AdminPage title="Feature Flags">
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5">Feature Flags</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Create Flag
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flags.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        No feature flags configured
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  flags.map((flag) => (
                    <TableRow key={flag.id}>
                      <TableCell>
                        <Chip label={flag.name} size="small" />
                      </TableCell>
                      <TableCell>{flag.description || "-"}</TableCell>
                      <TableCell>
                        <Switch
                          checked={flag.enabled}
                          onChange={() => handleToggleEnabled(flag)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {JSON.stringify(flag.value)}
                        </Typography>
                      </TableCell>
                      <TableCell>{getTargetDescription(flag)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(flag)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedFlag(flag);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create/Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingFlag ? "Edit Feature Flag" : "Create Feature Flag"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <Autocomplete
                freeSolo
                options={Array.from(new Set(flags.map((f) => f.name)))}
                value={formName}
                onInputChange={(_e, newValue) => setFormName(newValue)}
                disabled={!!editingFlag}
                renderInput={(params) => (
                  <TextField {...params} label="Flag Name" />
                )}
              />

              <TextField
                label="Description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />

              <TextField
                label="Value (JSON)"
                value={formValue}
                onChange={(e) => setFormValue(e.target.value)}
                fullWidth
                helperText='The value returned when the flag is active. e.g. true, "some string", {"key": "val"}'
              />

              <FormControl fullWidth>
                <InputLabel>Target Type</InputLabel>
                <Select
                  value={formTargetType}
                  label="Target Type"
                  onChange={(e) => setFormTargetType(e.target.value as TargetType)}
                >
                  <MenuItem value="everyone">Everyone</MenuItem>
                  <MenuItem value="admins">Admins Only</MenuItem>
                  <MenuItem value="userList">Specific Users</MenuItem>
                  <MenuItem value="permissionGroup">Permission Group</MenuItem>
                </Select>
              </FormControl>

              {formTargetType === "userList" && (
                <Autocomplete
                  multiple
                  options={allUsers}
                  getOptionLabel={(opt) => `${opt.email} (${opt.name})`}
                  value={allUsers.filter((u) => formTargetUserIds.includes(u.id))}
                  onChange={(_e, newValue) =>
                    setFormTargetUserIds(newValue.map((u) => u.id))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Target Users" />
                  )}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                />
              )}

              {formTargetType === "permissionGroup" && (
                <FormControl fullWidth>
                  <InputLabel>Permission Group</InputLabel>
                  <Select
                    value={formTargetGroupId}
                    label="Permission Group"
                    onChange={(e) => setFormTargetGroupId(e.target.value)}
                  >
                    {permissionGroups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  checked={formEnabled}
                  onChange={(e) => setFormEnabled(e.target.checked)}
                />
                <Typography>
                  {formEnabled ? "Enabled" : "Disabled"}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!formName.trim()}
            >
              {editingFlag ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Delete Feature Flag</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the flag "{selectedFlag?.name}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminPage>
  );
}
